import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { DateRange } from "@act-group/act-ui-library";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { VerificationsGrid } from "../../../components/VerificationsGrid";
import { useVerifications } from "../../../hooks/useVerifications";
import {
  isTemporarilyHiddenVerification,
  isUntouchedVerification,
} from "../../../types/verification";

const PENDING_LOOKBACK_DAYS = 30;

function KycPage() {
  const { t } = useTranslation();
  const dateRange = useMemo<DateRange>(
    () => ({ start: dayjs().subtract(PENDING_LOOKBACK_DAYS, "day").startOf("day"), end: dayjs() }),
    []
  );
  const { rows, rowCount, isLoading, error, paginationModel, setPaginationModel } =
    useVerifications({ dateRange, status: "Pending" });

  // Hide untouched verifications (see isUntouchedVerification) — same rule as
  // the Dashboard grid, so a freshly created record with no decision yet
  // doesn't show up here either. Also applies the same TEMPORARY name-based
  // hide (isTemporarilyHiddenVerification) for consistency across grids.
  const pendingRows = useMemo(
    () =>
      rows.filter((row) => !isUntouchedVerification(row) && !isTemporarilyHiddenVerification(row)),
    [rows]
  );

  return (
    <Stack spacing={3}>
      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>
        {t("Pending")}
      </Typography>

      <VerificationsGrid
        rows={pendingRows}
        rowCount={rowCount}
        isLoading={isLoading}
        error={error}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />
    </Stack>
  );
}

export default KycPage;
